export const PERSISTENT_CART_CACHE_VERSION = 4;

const asArray = (value) => (Array.isArray(value) ? value : []);

const hasCartItems = (entry) =>
  asArray(entry?.items).length + asArray(entry?.pendingItems).length > 0;

const normalizeCourses = (value) =>
  asArray(value)
    .map((course) => {
      const courseNumber = Math.max(
        1,
        Number(course?.courseNumber ?? course?.course_number ?? 1) || 1,
      );
      return {
        id: course?.id ?? null,
        // Both aliases are intentional: older POS code reads snake_case while
        // the persistent cache historically stored camelCase.
        courseNumber,
        course_number: courseNumber,
        status: ["held", "fired", "completed", "cancelled"].includes(
          String(course?.status || "").toLowerCase(),
        )
          ? String(course.status).toLowerCase()
          : "held",
      };
    })
    .filter(
      (course, index, courses) =>
        courses.findIndex(
          (candidate) => candidate.courseNumber === course.courseNumber,
        ) === index,
    );

export const readPersistentCartCachePayload = (
  raw,
  { now = Date.now(), maxAgeMs } = {},
) => {
  const map = new Map();
  if (!raw) return map;

  const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
  const entries = Array.isArray(parsed?.entries) ? parsed.entries : [];
  entries.forEach((entry) => {
    const key = String(entry?.key || "");
    const timestamp = Number(entry?.timestamp || 0);
    const normalized = {
      items: asArray(entry?.items),
      pendingItems: asArray(entry?.pendingItems),
      isDelivery: Boolean(entry?.isDelivery),
      timestamp: timestamp || now,
      hash: String(entry?.hash || ""),
      courseManagementEnabled: Boolean(entry?.courseManagementEnabled),
      activeCourseNumber: Math.max(
        1,
        Number(entry?.activeCourseNumber || 1) || 1,
      ),
      courses: normalizeCourses(entry?.courses),
      recoveryRequired: Boolean(entry?.recoveryRequired),
      recoveryReason: String(entry?.recoveryReason || ""),
      occupancyCycle: Math.max(1, Number(entry?.occupancyCycle || 1) || 1),
      cacheContextId: String(entry?.cacheContextId || ""),
      detachedIdentity: entry?.detachedIdentity || null,
    };
    if (!key || !hasCartItems(normalized)) return;
    if (
      Number.isFinite(maxAgeMs) &&
      maxAgeMs > 0 &&
      timestamp &&
      now - timestamp > maxAgeMs &&
      normalized.pendingItems.length === 0
      && !key.startsWith("preserved:")
    ) {
      return;
    }
    map.set(key, normalized);
  });
  return map;
};

export const buildPersistentCartCachePayload = (
  map,
  { now = Date.now(), maxAgeMs } = {},
) => {
  const entries = [];
  if (!(map instanceof Map)) {
    return { version: PERSISTENT_CART_CACHE_VERSION, updatedAt: now, entries };
  }

  map.forEach((value, key) => {
    const timestamp = Number(value?.timestamp || 0);
    const normalized = {
      key: String(key || ""),
      items: asArray(value?.items),
      pendingItems: asArray(value?.pendingItems),
      isDelivery: Boolean(value?.isDelivery),
      timestamp: timestamp || now,
      hash: String(value?.hash || ""),
      courseManagementEnabled: Boolean(value?.courseManagementEnabled),
      activeCourseNumber: Math.max(
        1,
        Number(value?.activeCourseNumber || 1) || 1,
      ),
      courses: normalizeCourses(value?.courses),
      recoveryRequired: Boolean(value?.recoveryRequired),
      recoveryReason: String(value?.recoveryReason || ""),
      occupancyCycle: Math.max(1, Number(value?.occupancyCycle || 1) || 1),
      cacheContextId: String(value?.cacheContextId || ""),
      detachedIdentity: value?.detachedIdentity || null,
    };
    if (!normalized.key || !hasCartItems(normalized)) return;
    if (
      Number.isFinite(maxAgeMs) &&
      maxAgeMs > 0 &&
      timestamp &&
      now - timestamp > maxAgeMs &&
      normalized.pendingItems.length === 0
      && !normalized.key.startsWith("preserved:")
    ) {
      return;
    }
    entries.push(normalized);
  });

  return {
    version: PERSISTENT_CART_CACHE_VERSION,
    updatedAt: now,
    entries,
  };
};

export const upsertPersistentCartCacheEntry = (
  map,
  key,
  patch = {},
  { now = Date.now() } = {},
) => {
  if (!(map instanceof Map) || !key) return null;
  const previous = map.get(key) || {};
  const next = {
    ...previous,
    ...patch,
    cacheContextId: String(patch.cacheContextId ?? previous.cacheContextId ?? ""),
    items:
      patch.items === undefined
        ? asArray(previous.items)
        : asArray(patch.items),
    pendingItems:
      patch.pendingItems === undefined
        ? asArray(previous.pendingItems)
        : asArray(patch.pendingItems),
    timestamp: Number(patch.timestamp || now),
    courseManagementEnabled:
      patch.courseManagementEnabled === undefined
        ? Boolean(previous.courseManagementEnabled)
        : Boolean(patch.courseManagementEnabled),
    activeCourseNumber: Math.max(
      1,
      Number(
        patch.activeCourseNumber === undefined
          ? previous.activeCourseNumber || 1
          : patch.activeCourseNumber,
      ) || 1,
    ),
    courses:
      patch.courses === undefined
        ? normalizeCourses(previous.courses)
        : normalizeCourses(patch.courses),
    recoveryRequired:
      patch.recoveryRequired === undefined
        ? Boolean(previous.recoveryRequired)
        : Boolean(patch.recoveryRequired),
    recoveryReason:
      patch.recoveryReason === undefined
        ? String(previous.recoveryReason || "")
        : String(patch.recoveryReason || ""),
    occupancyCycle: Math.max(
      1,
      Number(
        patch.occupancyCycle === undefined
          ? previous.occupancyCycle || 1
          : patch.occupancyCycle,
      ) || 1,
    ),
  };
  if (!hasCartItems(next)) {
    map.delete(key);
    return null;
  }
  map.set(key, next);
  return next;
};

export const clearPersistentSentItems = (
  map,
  key,
  { now = Date.now() } = {},
) => {
  if (!(map instanceof Map) || !key) return null;
  const previous = map.get(key);
  if (!previous) return null;
  const pendingItems = asArray(previous.pendingItems);
  if (pendingItems.length === 0) {
    map.delete(key);
    return null;
  }
  const next = {
    ...previous,
    items: [],
    pendingItems,
    hash: "",
    timestamp: now,
  };
  map.set(key, next);
  return next;
};
